import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../AuthContext';
import { useAccounts } from '../lib/useAccounts';
import { allQuotes } from '../lib/salesData';
import { usd } from '../lib/clientDeepData';

const STATUSES = ['all', 'draft', 'sent', 'viewed', 'negotiating'] as const;

export default function QuoteRegistry() {
  const { demo } = useAuth();
  const { data, loading } = useAccounts(demo);
  const nav = useNavigate();
  const [q, setQ] = useState('');
  const [status, setStatus] = useState<(typeof STATUSES)[number]>('all');

  const quotes = useMemo(() => allQuotes(data?.accounts ?? []), [data]);
  const rows = useMemo(() => {
    let r = quotes;
    if (status !== 'all') r = r.filter((x) => x.status === status);
    if (q.trim()) { const t = q.toLowerCase(); r = r.filter((x) => x.clientName.toLowerCase().includes(t) || x.id.toLowerCase().includes(t) || x.product.toLowerCase().includes(t)); }
    return r;
  }, [quotes, q, status]);

  if (loading) return <div className="loading"><div className="spinner" /> Loading quotes…</div>;
  const totalValue = quotes.reduce((s, x) => s + x.value, 0);
  const avgMargin = quotes.length ? Math.round((quotes.reduce((s, x) => s + x.marginPct, 0) / quotes.length) * 100) : 0;
  const shown = rows.slice(0, 400);

  return (
    <>
      <div className="page-head">
        <div><div className="page-title">Quote Registry</div><div className="page-sub">Every quote ever sent — status, value, margin, and follow-up history.</div></div>
        <button className="btn primary" onClick={() => nav('/quote-engine')}>＋ New quote</button>
      </div>

      <div className="grid cols-4" style={{ marginBottom: 16 }}>
        <div className="metric-card"><div className="metric-label">Total Quotes</div><div className="metric-value" style={{ color: 'var(--ac)' }}>{quotes.length}</div><div className="metric-note">on record</div></div>
        <div className="metric-card"><div className="metric-label">Quoted Value</div><div className="metric-value">{usd(totalValue)}</div><div className="metric-note">across all quotes</div></div>
        <div className="metric-card"><div className="metric-label">Avg Margin</div><div className="metric-value" style={{ color: 'var(--gn)' }}>{avgMargin}%</div><div className="metric-note">blended</div></div>
        <div className="metric-card"><div className="metric-label">Open</div><div className="metric-value" style={{ color: 'var(--yl)' }}>{quotes.filter((x) => x.status !== 'draft').length}</div><div className="metric-note">sent / in play</div></div>
      </div>

      <div className="toolbar">
        <input className="search" placeholder="Search quote, client, product…" value={q} onChange={(e) => setQ(e.target.value)} />
        <div className="seg">{STATUSES.map((s) => <button key={s} className={status === s ? 'on' : ''} onClick={() => setStatus(s)}>{s === 'all' ? 'All' : s[0].toUpperCase() + s.slice(1)}</button>)}</div>
      </div>
      <div className="page-sub" style={{ marginBottom: 10 }}>{rows.length} quotes{rows.length > 400 ? ' (showing 400)' : ''}</div>

      <div className="table-wrap"><table>
        <thead><tr><th>Quote</th><th>Client</th><th>Product</th><th>Value</th><th>Margin</th><th>Status</th><th>Age</th></tr></thead>
        <tbody>
          {shown.map((x) => (
            <tr key={x.id + x.clientId} onClick={() => nav(`/client?id=${encodeURIComponent(x.clientId)}`)}>
              <td><strong>{x.id}</strong></td>
              <td>{x.clientName}</td>
              <td style={{ color: 'var(--tx2)' }}>{x.product} · {x.qty}</td>
              <td>{usd(x.value)}</td>
              <td style={{ color: 'var(--gn)' }}>{Math.round(x.marginPct * 100)}% · {usd(x.margin)}</td>
              <td><span className={`tag pr-${x.status === 'negotiating' ? 'rush' : 'standard'}`}>{x.status}</span></td>
              <td>{x.age}</td>
            </tr>
          ))}
          {rows.length === 0 && <tr><td colSpan={7}><div className="empty">No quotes match.</div></td></tr>}
        </tbody>
      </table></div>
    </>
  );
}
